// AI Generated Component
import { Component, OnInit, Input } from '@angular/core';
import { ControlContainer, NgForm } from '@angular/forms';
import { safeClone } from '@client/app/shared/utils/object-utils';

interface Rule {
    operator: string;
    value: string;
}

interface CmsOption {
    name: string;
    id: string;
}

@Component({
    selector: 'form-source-code-exclude-sce',
    standalone: false,
    viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
    template: `
        <fieldset [ngModelGroup]="modelGroup" #sceGroup="ngModelGroup">
            <div class="card panel-monsido-card">
                <div class="card-header">
                    <h3 class="font-weight-300" translate>Details</h3>
                </div>
                <div class="card-body">
                    <form-field-input [name]="'title'"
                                      [label]="'Description' | translate"
                                      [placeholder]="'Describe the exclude' | translate"
                                      type="text"
                                      [(ngModel)]="model.title"
                                      #title="ngModel"
                                      [required]="true">
                    </form-field-input>
                    <form-field-errors [input]="title.control">
                    </form-field-errors>
                    <form-field-select [name]="'cms'"
                                       [(ngModel)]="model.default_for_cms"
                                       [options]="cmses"
                                       [label]="'Default for CMS' | translate"
                                       [removeButton]="true"
                                       returnValue="id"
                                       [defaultOption]="'None' | translate">
                    </form-field-select>
                    <div class="row form-group"
                         *ngFor="let rule of getVisibleRules(); let i = index; trackBy: trackByIndex">
                        <label class="col-sm-8 col-form-label text-bold text-right" [for]="'rule_val_' + i" translate>CSS Target Selector</label>
                        <div class="col-sm pr-0">
                            <input class="form-control"
                                   type="text"
                                   [name]="'rule_val_' + i"
                                   [id]="'rule_val_' + i"
                                   [(ngModel)]="rule.value"
                                   monValidateCssSelector
                                   required/>
                            <span *ngIf="sceGroup?.control?.controls?.['rule_val_' + i]?.errors?.['required']" class="invalid-feedback block" translate>This field is required</span>
                            <span *ngIf="sceGroup?.control?.controls?.['rule_val_' + i]?.errors?.['cssSelector']" class="invalid-feedback block" translate>Not a valid CSS selector</span>
                        </div>
                        <div class="col-auto">
                            <button class="btn btn-border-radius btn-secondary pt-2"
                                    type="button"
                                    (click)="removeRule(i)">
                                <span class="text-link" [ngClass]="['ACTIONS', 'REMOVE'] | monIcons"></span>
                                <span class="sr-only" translate>Remove Rule</span>
                            </button>
                        </div>
                    </div>
                    <div class="row">
                        <div class="offset-8 col-auto mr-auto">
                            <button type="button"
                                    class="btn btn-border-radius btn-secondary"
                                    (click)="addRule()">
                                <span class="text-link" [ngClass]="['ACTIONS', 'ADD'] | monIcons" aria-hidden="true"></span>
                                <span class="sr-only" translate>Add Rule</span>
                            </button>
                        </div>
                        <div class="col-auto" *ngIf="model.rules.length > pageSize">
                            <ngb-pagination
                                [disabled]="model.rules.length <= pageSize"
                                [collectionSize]="model.rules.length"
                                [(page)]="page"
                                [pageSize]="pageSize"
                                [maxSize]="4"
                                [boundaryLinks]="false"
                                size="sm">
                            </ngb-pagination>
                        </div>
                    </div>
                </div>
            </div>
        </fieldset>
    `,
})
export class FormSourceCodeExcludeSceComponent implements OnInit {
    @Input() modelGroup = '';
    @Input() model;
    pageSize = 10;
    page = 1;
    cmses: CmsOption[] = [];

    ngOnInit (): void {
        this.initializeCmses();
        this.initializeDefaultValues();
    }

    private initializeCmses (): void {
        this.cmses = safeClone([
            {
                name: 'CivicEngage Central',
                id: 'civicengage_central',
            },
            {
                name: 'CivicEngage Evolve',
                id: 'civicengage_evolve',
            },
            {
                name: 'Web Open',
                id: 'civicengage_open',
            },
            {
                name: 'Craft CMS',
                id: 'craft',
            },
            {
                name: 'SIMsite',
                id: 'simsite',
            },
            {
                name: 'Adobe CMS',
                id: 'adobe',
            },
            {
                name: 'Konform',
                id: 'konform',
            },
            {
                name: 'Acos',
                id: 'acos',
            },
            {
                name: 'AEM',
                id: 'aem',
            },
            {
                name: 'Shopify',
                id: 'shopify',
            },
            {
                name: 'EPiServer',
                id: 'episerver',
            },
            {
                name: 'iCreate',
                id: 'icreate',
            },
            {
                name: 'Elcom',
                id: 'elcom',
            },
            {
                name: 'Cascade',
                id: 'cascade',
            },
            {
                name: 'CorePublish',
                id: 'corepublish',
            },
            {
                name: 'Custom CMS',
                id: 'custom',
            },
            {
                name: 'Sitecore',
                id: 'sitecore',
            },
            {
                name: 'Dynamicweb',
                id: 'dynamicweb',
            },
            {
                name: 'Umbraco',
                id: 'umbraco',
            },
            {
                name: 'Custom meta header',
                id: 'special',
            },
            {
                name: 'WordPress',
                id: 'wordpress',
            },
            {
                name: 'WordPress Multisite',
                id: 'wordpress_multisite',
            },
            {
                name: 'Omniupdate',
                id: 'omniupdate',
            },
            {
                name: 'Publicera',
                id: 'publicera',
            },
            {
                name: 'Drupal',
                id: 'drupal',
            },
            {
                name: 'Finalsite',
                id: 'finalsite',
            },
            {
                name: 'Kentico',
                id: 'kentico',
            },
            {
                name: 'Municode',
                id: 'municode',
            },
            {
                name: 'Presence',
                id: 'presence',
            },
            {
                name: 'Gabbart',
                id: 'gabbart',
            },
            {
                name: 'Hubspot',
                id: 'hubspot',
            },
            {
                name: 'Squarespace',
                id: 'squarespace',
            },
            {
                name: 'Squiz',
                id: 'squiz',
            },
            {
                name: 'SilverStripe',
                id: 'silverstripe',
            },
            {
                name: 'Sitefinity',
                id: 'sitefinity',
            },
            {
                name: 'Sitevision',
                id: 'sitevision',
            },
            {
                name: 'Plone',
                id: 'plone',
            },
            {
                name: 'OpenCities',
                id: 'opencities',
            },
            {
                name: 'Joomla',
                id: 'joomla',
            },
            {
                name: 'Smart Sites',
                id: 'smart_sites',
            },
            {
                name: 'BayWSF',
                id: 'baywsf',
            },
        ].sort((a, b) => a.name > b.name ? 1 : a.name < b.name ? -1 : 0));
    }

    private initializeDefaultValues (): void {
        if (!Array.isArray(this.model.rules)) {
            this.model.rules = [];
        }

        if (typeof this.model.id !== 'number' && this.model.rules.length === 0) {
            this.addRule();
        }
    }

    addRule (): void {
        this.model.rules.unshift({ operator: 'css', value: '' });
        this.page = 1;
    }

    removeRule (index: number): void {
        const actualIndex = this.pageSize * (this.page - 1) + index;
        this.model.rules.splice(actualIndex, 1);
    }

    getVisibleRules (): Rule[] {
        const startIndex = this.pageSize * (this.page - 1);
        return this.model.rules.slice(startIndex, startIndex + this.pageSize);
    }

    trackByIndex (index: number): number {
        return index;
    }
}
